import { z } from "zod";

export const SOCIAL_POST_PLATFORMS = ["instagram", "tiktok"] as const;
export type SocialPostPlatform = (typeof SOCIAL_POST_PLATFORMS)[number];
export const socialPostPlatformSchema = z.enum(SOCIAL_POST_PLATFORMS);

export const INSTAGRAM_SINGLE_SOCIAL_POST_TYPES = [
  "feed_portrait",
  "feed_square",
  "story",
] as const;
export type InstagramSingleSocialPostType = (typeof INSTAGRAM_SINGLE_SOCIAL_POST_TYPES)[number];

export const INSTAGRAM_CAROUSEL_SOCIAL_POST_TYPES = [
  "carousel_portrait",
  "carousel_square",
] as const;
export type InstagramCarouselSocialPostType = (typeof INSTAGRAM_CAROUSEL_SOCIAL_POST_TYPES)[number];

export const INSTAGRAM_SOCIAL_POST_TYPES = [
  ...INSTAGRAM_SINGLE_SOCIAL_POST_TYPES,
  ...INSTAGRAM_CAROUSEL_SOCIAL_POST_TYPES,
] as const;

export const TIKTOK_SINGLE_SOCIAL_POST_TYPES = ["tiktok_portrait"] as const;
export type TikTokSingleSocialPostType = (typeof TIKTOK_SINGLE_SOCIAL_POST_TYPES)[number];

export const TIKTOK_CAROUSEL_SOCIAL_POST_TYPES = ["tiktok_carousel_portrait"] as const;
export type TikTokCarouselSocialPostType = (typeof TIKTOK_CAROUSEL_SOCIAL_POST_TYPES)[number];

export const TIKTOK_SOCIAL_POST_TYPES = [
  ...TIKTOK_SINGLE_SOCIAL_POST_TYPES,
  ...TIKTOK_CAROUSEL_SOCIAL_POST_TYPES,
] as const;

export const SOCIAL_POST_SINGLE_TYPES = [
  ...INSTAGRAM_SINGLE_SOCIAL_POST_TYPES,
  ...TIKTOK_SINGLE_SOCIAL_POST_TYPES,
] as const;
export type SocialPostSingleType = (typeof SOCIAL_POST_SINGLE_TYPES)[number];

export const SOCIAL_POST_CAROUSEL_TYPES = [
  ...INSTAGRAM_CAROUSEL_SOCIAL_POST_TYPES,
  ...TIKTOK_CAROUSEL_SOCIAL_POST_TYPES,
] as const;
export type SocialPostCarouselType = (typeof SOCIAL_POST_CAROUSEL_TYPES)[number];

export const SOCIAL_POST_TYPES = [
  ...INSTAGRAM_SINGLE_SOCIAL_POST_TYPES,
  ...INSTAGRAM_CAROUSEL_SOCIAL_POST_TYPES,
  ...TIKTOK_SINGLE_SOCIAL_POST_TYPES,
  ...TIKTOK_CAROUSEL_SOCIAL_POST_TYPES,
] as const;
export type SocialPostType = (typeof SOCIAL_POST_TYPES)[number];
export const socialPostTypeSchema = z.enum(SOCIAL_POST_TYPES);

export const instagramSocialPostTypeSchema = z.enum(INSTAGRAM_SOCIAL_POST_TYPES);
export const socialPostCarouselTypeSchema = z.enum(SOCIAL_POST_CAROUSEL_TYPES);
export const socialPostSingleTypeSchema = z.enum(SOCIAL_POST_SINGLE_TYPES);

export const SOCIAL_POST_CANVAS_SIZE_REGISTRY = {
  instagram: {
    feed_portrait: { width: 1080, height: 1350 },
    feed_square: { width: 1080, height: 1080 },
    story: { width: 1080, height: 1920 },
    carousel_portrait: { width: 1080, height: 1350 },
    carousel_square: { width: 1080, height: 1080 },
  },
  tiktok: {
    tiktok_portrait: { width: 1080, height: 1920 },
    tiktok_carousel_portrait: { width: 1080, height: 1920 },
  },
} as const satisfies Record<
  SocialPostPlatform,
  Partial<Record<SocialPostType, { width: number; height: number }>>
>;

export interface SocialPostCanvasSize {
  width: number;
  height: number;
}

export const getSocialPostCanvasSize = (
  platform: SocialPostPlatform,
  postType: SocialPostType,
): SocialPostCanvasSize | null => {
  const platformSizes = SOCIAL_POST_CANVAS_SIZE_REGISTRY[platform] as Partial<
    Record<SocialPostType, SocialPostCanvasSize>
  >;
  const size = platformSizes[postType];
  if (!size) {
    return null;
  }

  return {
    width: size.width,
    height: size.height,
  };
};

export const getDefaultSocialPostPlatformForType = (
  postType: SocialPostType,
): SocialPostPlatform | null => {
  for (const platform of SOCIAL_POST_PLATFORMS) {
    if (getSocialPostCanvasSize(platform, postType)) {
      return platform;
    }
  }

  return null;
};

export const isSocialPostTypeSupportedByPlatform = (
  platform: SocialPostPlatform,
  postType: SocialPostType,
): boolean => getSocialPostCanvasSize(platform, postType) !== null;

export const isCarouselSocialPostType = (postType: string): postType is SocialPostCarouselType =>
  (SOCIAL_POST_CAROUSEL_TYPES as readonly string[]).includes(postType);

export const isSingleSocialPostType = (postType: string): postType is SocialPostSingleType =>
  (SOCIAL_POST_SINGLE_TYPES as readonly string[]).includes(postType);

export const getCarouselBaseFeedPostType = (
  postType: SocialPostCarouselType,
): Extract<SocialPostSingleType, "feed_portrait" | "feed_square" | "tiktok_portrait"> => {
  if (postType === "carousel_portrait") {
    return "feed_portrait";
  }

  if (postType === "carousel_square") {
    return "feed_square";
  }

  return "tiktok_portrait";
};

const nonEmptyStringSchema = z.string().trim().min(1);
const colorStringSchema = nonEmptyStringSchema;

export const SOCIAL_POST_FONT_SOURCES = ["brand_confirmed", "google_font"] as const;
export type SocialPostFontSource = (typeof SOCIAL_POST_FONT_SOURCES)[number];
export const socialPostFontSourceSchema = z.enum(SOCIAL_POST_FONT_SOURCES);

export const SOCIAL_POST_FONT_ROLES = ["heading", "body"] as const;
export type SocialPostFontRole = (typeof SOCIAL_POST_FONT_ROLES)[number];
export const socialPostFontRoleSchema = z.enum(SOCIAL_POST_FONT_ROLES);

export const SOCIAL_POST_TEXT_ALIGNS = ["left", "center", "right", "justify"] as const;
export type SocialPostTextAlign = (typeof SOCIAL_POST_TEXT_ALIGNS)[number];
export const socialPostTextAlignSchema = z.enum(SOCIAL_POST_TEXT_ALIGNS);

export const SOCIAL_POST_VERTICAL_ALIGNS = ["top", "middle", "bottom"] as const;
export type SocialPostVerticalAlign = (typeof SOCIAL_POST_VERTICAL_ALIGNS)[number];
export const socialPostVerticalAlignSchema = z.enum(SOCIAL_POST_VERTICAL_ALIGNS);

export const SOCIAL_POST_TEXT_WRAPS = ["word", "char", "none"] as const;
export type SocialPostTextWrap = (typeof SOCIAL_POST_TEXT_WRAPS)[number];
export const socialPostTextWrapSchema = z.enum(SOCIAL_POST_TEXT_WRAPS);

export const SOCIAL_POST_FONT_STYLES = ["normal", "bold", "italic", "bold italic"] as const;
export type SocialPostFontStyle = (typeof SOCIAL_POST_FONT_STYLES)[number];
export const socialPostFontStyleSchema = z.enum(SOCIAL_POST_FONT_STYLES);

export const SOCIAL_POST_IMAGE_FITS = ["contain", "cover", "fill"] as const;
export type SocialPostImageFit = (typeof SOCIAL_POST_IMAGE_FITS)[number];
export const socialPostImageFitSchema = z.enum(SOCIAL_POST_IMAGE_FITS);

export const SOCIAL_POST_SHAPE_TYPES = [
  "ellipse",
  "line",
  "polygon",
  "star",
  "ring",
  "arc",
  "path",
] as const;
export type SocialPostShapeType = (typeof SOCIAL_POST_SHAPE_TYPES)[number];
export const socialPostShapeTypeSchema = z.enum(SOCIAL_POST_SHAPE_TYPES);

export const SOCIAL_POST_IMAGE_BACKGROUND_MODES = [
  "opaque",
  "transparent_subject",
  "transparent_graphic",
] as const;
export type SocialPostImageBackgroundMode = (typeof SOCIAL_POST_IMAGE_BACKGROUND_MODES)[number];
export const socialPostImageBackgroundModeSchema = z.enum(SOCIAL_POST_IMAGE_BACKGROUND_MODES);

const socialPostSceneFontBaseV1Schema = z.object({
  role: socialPostFontRoleSchema,
  family: nonEmptyStringSchema,
  selectedVariant: nonEmptyStringSchema,
});

export const socialPostSceneBrandFontV1Schema = socialPostSceneFontBaseV1Schema.extend({
  source: z.literal("brand_confirmed"),
  assetObjectKey: nonEmptyStringSchema,
});

export const socialPostSceneGoogleFontV1Schema = socialPostSceneFontBaseV1Schema.extend({
  source: z.literal("google_font"),
});

export const socialPostSceneFontV1Schema = z.discriminatedUnion("source", [
  socialPostSceneBrandFontV1Schema,
  socialPostSceneGoogleFontV1Schema,
]);
export type SocialPostSceneFontV1 = z.infer<typeof socialPostSceneFontV1Schema>;

export const socialPostSceneCanvasV1Schema = z.object({
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  backgroundFill: colorStringSchema,
});
export type SocialPostSceneCanvasV1 = z.infer<typeof socialPostSceneCanvasV1Schema>;

const socialPostSceneBlockBaseV1Schema = z.object({
  id: nonEmptyStringSchema,
  layerName: nonEmptyStringSchema.max(80).optional(),
  x: z.number().finite(),
  y: z.number().finite(),
  width: z.number().finite().positive(),
  height: z.number().finite().positive(),
  rotation: z.number().finite().default(0),
  opacity: z.number().finite().min(0).max(1).default(1),
  zIndex: z.number().int().nonnegative().default(0),
  visible: z.boolean().default(true),
});

export const socialPostGeneratedImageSourceV1Schema = z.object({
  kind: z.literal("generated_prompt"),
  prompt: nonEmptyStringSchema,
  backgroundMode: socialPostImageBackgroundModeSchema.default("opaque"),
});
export type SocialPostGeneratedImageSourceV1 = z.infer<
  typeof socialPostGeneratedImageSourceV1Schema
>;

export const socialPostSceneTextBlockV1Schema = socialPostSceneBlockBaseV1Schema.extend({
  type: z.literal("text"),
  text: nonEmptyStringSchema,
  fill: colorStringSchema,
  stroke: colorStringSchema.optional(),
  strokeWidth: z.number().finite().nonnegative().optional(),
  fontFamily: nonEmptyStringSchema,
  fontSize: z.number().finite().positive(),
  fontStyle: socialPostFontStyleSchema.default("normal"),
  align: socialPostTextAlignSchema.default("left"),
  verticalAlign: socialPostVerticalAlignSchema.default("top"),
  lineHeight: z.number().finite().positive().default(1.2),
  letterSpacing: z.number().finite().default(0),
  padding: z.number().finite().nonnegative().default(0),
});
export type SocialPostSceneTextBlockV1 = z.infer<typeof socialPostSceneTextBlockV1Schema>;

export const socialPostSceneImageBlockV1Schema = socialPostSceneBlockBaseV1Schema.extend({
  type: z.literal("image"),
  source: socialPostGeneratedImageSourceV1Schema,
  fit: socialPostImageFitSchema.default("cover"),
  cornerRadius: z.number().finite().nonnegative().default(0),
});
export type SocialPostSceneImageBlockV1 = z.infer<typeof socialPostSceneImageBlockV1Schema>;

export const socialPostSceneLogoBlockV1Schema = socialPostSceneBlockBaseV1Schema.extend({
  type: z.literal("logo"),
  source: z.literal("brand_logo").default("brand_logo"),
  assetObjectKey: nonEmptyStringSchema.optional(),
  fit: socialPostImageFitSchema.default("contain"),
  cornerRadius: z.number().finite().nonnegative().default(0),
});
export type SocialPostSceneLogoBlockV1 = z.infer<typeof socialPostSceneLogoBlockV1Schema>;

export const socialPostSceneRectBlockV1Schema = socialPostSceneBlockBaseV1Schema.extend({
  type: z.literal("rect"),
  fill: colorStringSchema,
  stroke: colorStringSchema.optional(),
  strokeWidth: z.number().finite().nonnegative().default(0),
  cornerRadius: z.number().finite().nonnegative().default(0),
});
export type SocialPostSceneRectBlockV1 = z.infer<typeof socialPostSceneRectBlockV1Schema>;

export const socialPostSceneShapeBlockV1Schema = socialPostSceneBlockBaseV1Schema
  .extend({
    type: z.literal("shape"),
    shapeType: socialPostShapeTypeSchema,
    fill: colorStringSchema.optional(),
    stroke: colorStringSchema.optional(),
    strokeWidth: z.number().finite().nonnegative().default(0),
    cornerRadius: z.number().finite().nonnegative().default(0),
    lineCap: z.enum(["butt", "round", "square"]).optional(),
    lineJoin: z.enum(["miter", "round", "bevel"]).optional(),
    dash: z.array(z.number().finite().nonnegative()).max(12).optional(),
    points: z.array(z.number().finite()).max(128).optional(),
    closed: z.boolean().optional(),
    tension: z.number().finite().min(0).max(1).optional(),
    sides: z.number().int().min(3).max(16).optional(),
    radius: z.number().finite().positive().optional(),
    innerRadius: z.number().finite().positive().optional(),
    outerRadius: z.number().finite().positive().optional(),
    angle: z.number().finite().optional(),
    clockwise: z.boolean().optional(),
    pathData: nonEmptyStringSchema.max(5000).optional(),
  })
  .superRefine((value, ctx) => {
    if (value.shapeType === "line") {
      if (!value.points || value.points.length < 4 || value.points.length % 2 !== 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["points"],
          message: "Line shape requires an even points array with at least 4 entries.",
        });
      }
    }

    if (value.shapeType === "polygon" && !value.sides) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["sides"],
        message: "Polygon shape requires sides.",
      });
    }

    if (value.shapeType === "star" && (!value.innerRadius || !value.outerRadius)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["innerRadius"],
        message: "Star shape requires innerRadius and outerRadius.",
      });
    }

    if (value.shapeType === "ring" && (!value.innerRadius || !value.outerRadius)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["innerRadius"],
        message: "Ring shape requires innerRadius and outerRadius.",
      });
    }

    if (value.shapeType === "arc" && (!value.innerRadius || !value.outerRadius || !value.angle)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["angle"],
        message: "Arc shape requires innerRadius, outerRadius, and angle.",
      });
    }

    if (value.shapeType === "path" && !value.pathData) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["pathData"],
        message: "Path shape requires pathData.",
      });
    }
  });
export type SocialPostSceneShapeBlockV1 = z.infer<typeof socialPostSceneShapeBlockV1Schema>;

export const socialPostSceneFrameBlockV1Schema = socialPostSceneBlockBaseV1Schema.extend({
  type: z.literal("frame"),
  fill: colorStringSchema.optional(),
  stroke: colorStringSchema,
  strokeWidth: z.number().finite().nonnegative().default(2),
  cornerRadius: z.number().finite().nonnegative().default(0),
  insetPadding: z.number().finite().nonnegative().default(0),
});
export type SocialPostSceneFrameBlockV1 = z.infer<typeof socialPostSceneFrameBlockV1Schema>;

export const socialPostSceneLeafBlockV1Schema = z.discriminatedUnion("type", [
  socialPostSceneTextBlockV1Schema,
  socialPostSceneImageBlockV1Schema,
  socialPostSceneLogoBlockV1Schema,
  socialPostSceneRectBlockV1Schema,
  socialPostSceneShapeBlockV1Schema,
  socialPostSceneFrameBlockV1Schema,
]);
export type SocialPostSceneLeafBlockV1 = z.infer<typeof socialPostSceneLeafBlockV1Schema>;

// ADR: Social post layouts are flat-only going forward. The group schema remains
// here only as a legacy read adapter for previously persisted plans/drafts; scene
// parsing immediately flattens group children into root blocks so all runtime
// workflows edit, render, publish, and re-save flat layer arrays.
export const socialPostSceneGroupBlockV1Schema = socialPostSceneBlockBaseV1Schema
  .extend({
    type: z.literal("group"),
    clip: z.boolean().default(false),
    padding: z.number().finite().nonnegative().default(0),
    children: z.array(socialPostSceneLeafBlockV1Schema).min(1).max(24),
  })
  .superRefine((value, ctx) => {
    const childIds = new Set<string>();
    let childLogoCount = 0;

    for (const [index, child] of value.children.entries()) {
      if (childIds.has(child.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["children", index, "id"],
          message: "Group child ids must be unique.",
        });
      }
      childIds.add(child.id);

      if (child.type === "logo") {
        childLogoCount += 1;
      }
    }

    if (childLogoCount > 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["children"],
        message: "Group supports at most one logo child.",
      });
    }
  });
export type SocialPostSceneGroupBlockV1 = z.infer<typeof socialPostSceneGroupBlockV1Schema>;

export const socialPostSceneBlockV1Schema = z.discriminatedUnion("type", [
  socialPostSceneTextBlockV1Schema,
  socialPostSceneImageBlockV1Schema,
  socialPostSceneLogoBlockV1Schema,
  socialPostSceneRectBlockV1Schema,
  socialPostSceneShapeBlockV1Schema,
  socialPostSceneFrameBlockV1Schema,
  socialPostSceneGroupBlockV1Schema,
]);
export type SocialPostSceneBlockV1 = z.infer<typeof socialPostSceneBlockV1Schema>;

const normalizeSceneBlockZIndexes = <T extends { zIndex: number }>(blocks: T[]): T[] =>
  blocks.map((block, index) => ({ ...block, zIndex: blocks.length - 1 - index }));

const rotateScenePoint = (x: number, y: number, degrees: number) => {
  const radians = (degrees * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);

  return {
    x: Math.round((x * cos - y * sin) * 1000) / 1000,
    y: Math.round((x * sin + y * cos) * 1000) / 1000,
  };
};

const legacyGroupChildToRootBlock = (
  group: SocialPostSceneGroupBlockV1,
  child: SocialPostSceneLeafBlockV1,
): SocialPostSceneLeafBlockV1 => {
  const position = rotateScenePoint(child.x, child.y, group.rotation);

  return socialPostSceneLeafBlockV1Schema.parse({
    ...child,
    x: group.x + position.x,
    y: group.y + position.y,
    rotation: group.rotation + child.rotation,
    opacity: group.opacity * child.opacity,
    visible: group.visible && child.visible,
  });
};

const flattenLegacySceneBlocks = (blocks: SocialPostSceneBlockV1[]): SocialPostSceneBlockV1[] => {
  if (!blocks.some((block) => block.type === "group")) {
    return blocks;
  }

  return normalizeSceneBlockZIndexes(
    blocks
      .toSorted((left, right) => right.zIndex - left.zIndex)
      .flatMap((block) =>
        block.type === "group"
          ? block.children
              .toSorted((left, right) => right.zIndex - left.zIndex)
              .map((child) => legacyGroupChildToRootBlock(block, child))
          : [block],
      ),
  );
};

export const socialPostSceneV1Schema = z
  .object({
    canvas: socialPostSceneCanvasV1Schema,
    fonts: z.array(socialPostSceneFontV1Schema).max(2),
    blocks: z.array(socialPostSceneBlockV1Schema).min(1).max(24),
  })
  .transform((value) => ({
    ...value,
    blocks: flattenLegacySceneBlocks(value.blocks),
  }))
  .superRefine((value, ctx) => {
    const fontFamilies = new Set(value.fonts.map((font) => font.family));
    const blockIds = new Set<string>();
    let logoCount = 0;

    if (value.blocks.length > 24) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["blocks"],
        message: "Scene supports at most 24 blocks after flattening legacy groups.",
      });
    }

    for (const [index, block] of value.blocks.entries()) {
      if (blockIds.has(block.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["blocks", index, "id"],
          message: "Block ids must be unique.",
        });
      }
      blockIds.add(block.id);

      for (const child of [block]) {
        if (child.type === "text" && !fontFamilies.has(child.fontFamily)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["blocks", index, "fontFamily"],
            message: "Text blocks must reference a declared scene font.",
          });
        }

        if (child.type === "logo") {
          logoCount += 1;
        }
      }
    }

    if (logoCount > 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["blocks"],
        message: "Scene supports at most one logo block.",
      });
    }
  });
export type SocialPostSceneV1 = z.infer<typeof socialPostSceneV1Schema>;

export const socialPostEmotionalBeatV1Schema = z.object({
  beat: nonEmptyStringSchema.max(160),
  register: nonEmptyStringSchema.max(160),
  isResolved: z.boolean(),
  mustSurviveInImage: z.boolean(),
});
export type SocialPostEmotionalBeatV1 = z.infer<typeof socialPostEmotionalBeatV1Schema>;

export const socialPostCreativeDirectionV1Schema = z.object({
  summary: nonEmptyStringSchema,
  objective: nonEmptyStringSchema,
  rationale: nonEmptyStringSchema,
  emotionalBeat: socialPostEmotionalBeatV1Schema.optional(),
});
export type SocialPostCreativeDirectionV1 = z.infer<typeof socialPostCreativeDirectionV1Schema>;

export const socialPostScenePlanV1Schema = z
  .object({
    type: z.literal("social_post.scene_plan"),
    version: z.literal(1),
    platform: socialPostPlatformSchema,
    postType: socialPostTypeSchema,
    creativeDirection: socialPostCreativeDirectionV1Schema,
    scene: socialPostSceneV1Schema,
  })
  .superRefine((value, ctx) => {
    const canvasSize = getSocialPostCanvasSize(value.platform, value.postType);
    if (!canvasSize) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["postType"],
        message: "Unsupported post type for platform.",
      });
      return;
    }

    if (value.scene.canvas.width !== canvasSize.width) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["scene", "canvas", "width"],
        message: `Canvas width must be ${canvasSize.width} for ${value.platform}/${value.postType}.`,
      });
    }

    if (value.scene.canvas.height !== canvasSize.height) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["scene", "canvas", "height"],
        message: `Canvas height must be ${canvasSize.height} for ${value.platform}/${value.postType}.`,
      });
    }

    const fontRoles = new Set(value.scene.fonts.map((font) => font.role));
    if (!fontRoles.has("heading")) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["scene", "fonts"],
        message: "Scene must declare a heading font.",
      });
    }

    if (!fontRoles.has("body")) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["scene", "fonts"],
        message: "Scene must declare a body font.",
      });
    }
  });
export type SocialPostScenePlanV1 = z.infer<typeof socialPostScenePlanV1Schema>;

export type SocialPostSceneSafetyIssue = {
  path: Array<string | number>;
  message: string;
};

const SAFE_CSS_COLOR_KEYWORD_PATTERN = /^[a-zA-Z]+$/;
const SAFE_CSS_HEX_COLOR_PATTERN = /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
const SAFE_FONT_FAMILY_PATTERN = /^[A-Za-z0-9 ._-]{1,100}$/;
const MAX_SAFE_SCENE_GEOMETRY_VALUE = 10_000;
const MAX_SAFE_SCENE_FONT_SIZE = 512;
const MAX_SAFE_SCENE_LINE_HEIGHT = 10;
const MAX_SAFE_SCENE_LETTER_SPACING = 200;
const MAX_SAFE_SCENE_SHAPE_POINTS = 10_000;

const unsafeCssTokenPattern = /[;"'{}<>\\]|\b(?:url|expression|var|calc|attr|import)\s*\(/i;

const isSafeCssNumber = (value: string, min: number, max: number): boolean => {
  if (!/^-?\d+(?:\.\d+)?%?$/.test(value)) {
    return false;
  }

  const isPercent = value.endsWith("%");
  const numeric = Number.parseFloat(isPercent ? value.slice(0, -1) : value);
  if (!Number.isFinite(numeric)) {
    return false;
  }

  if (isPercent) {
    return numeric >= 0 && numeric <= 100;
  }

  return numeric >= min && numeric <= max;
};

const isSafeCssAlpha = (value: string): boolean => {
  if (!/^\d+(?:\.\d+)?%?$/.test(value)) {
    return false;
  }

  const isPercent = value.endsWith("%");
  const numeric = Number.parseFloat(isPercent ? value.slice(0, -1) : value);
  if (!Number.isFinite(numeric)) {
    return false;
  }

  return isPercent ? numeric >= 0 && numeric <= 100 : numeric >= 0 && numeric <= 1;
};

const isSafeRgbColor = (value: string): boolean => {
  const match = /^rgba?\((.*)\)$/i.exec(value);
  if (!match) {
    return false;
  }

  const body = match[1] ?? "";
  const parts = body.split(",").map((part) => part.trim());
  if (parts.length !== 3 && parts.length !== 4) {
    return false;
  }

  return (
    parts.slice(0, 3).every((part) => isSafeCssNumber(part, 0, 255)) &&
    (parts[3] === undefined || isSafeCssAlpha(parts[3]))
  );
};

export const isSafeSocialPostCssColor = (value: string): boolean => {
  const trimmed = value.trim();
  if (trimmed.length === 0 || trimmed !== value || unsafeCssTokenPattern.test(trimmed)) {
    return false;
  }

  return (
    SAFE_CSS_HEX_COLOR_PATTERN.test(trimmed) ||
    isSafeRgbColor(trimmed) ||
    SAFE_CSS_COLOR_KEYWORD_PATTERN.test(trimmed)
  );
};

export const isSafeSocialPostFontFamily = (value: string): boolean => {
  const trimmed = value.trim();
  return (
    trimmed.length > 0 &&
    trimmed === value &&
    SAFE_FONT_FAMILY_PATTERN.test(trimmed) &&
    /[A-Za-z]/.test(trimmed)
  );
};

const pushSafeCssColorIssue = (
  issues: SocialPostSceneSafetyIssue[],
  path: Array<string | number>,
  value: string | undefined,
) => {
  if (value !== undefined && !isSafeSocialPostCssColor(value)) {
    issues.push({ path, message: "CSS color values must be simple safe color tokens." });
  }
};

const pushSafeFontFamilyIssue = (
  issues: SocialPostSceneSafetyIssue[],
  path: Array<string | number>,
  value: string,
) => {
  if (!isSafeSocialPostFontFamily(value)) {
    issues.push({ path, message: "Font family values must be simple safe font tokens." });
  }
};

const pushBoundedNumberIssue = (
  issues: SocialPostSceneSafetyIssue[],
  path: Array<string | number>,
  value: number,
  maxAbs = MAX_SAFE_SCENE_GEOMETRY_VALUE,
) => {
  if (!Number.isFinite(value) || Math.abs(value) > maxAbs) {
    issues.push({ path, message: `Scene geometry values must stay within ${maxAbs}px.` });
  }
};

export const collectSocialPostScenePlanSafetyIssues = (
  plan: SocialPostScenePlanV1,
): SocialPostSceneSafetyIssue[] => {
  const issues: SocialPostSceneSafetyIssue[] = [];

  pushBoundedNumberIssue(issues, ["scene", "canvas", "width"], plan.scene.canvas.width);
  pushBoundedNumberIssue(issues, ["scene", "canvas", "height"], plan.scene.canvas.height);
  pushSafeCssColorIssue(
    issues,
    ["scene", "canvas", "backgroundFill"],
    plan.scene.canvas.backgroundFill,
  );

  for (const [fontIndex, font] of plan.scene.fonts.entries()) {
    pushSafeFontFamilyIssue(issues, ["scene", "fonts", fontIndex, "family"], font.family);
  }

  for (const [blockIndex, block] of plan.scene.blocks.entries()) {
    const blockPath: Array<string | number> = ["scene", "blocks", blockIndex];

    pushBoundedNumberIssue(issues, [...blockPath, "x"], block.x);
    pushBoundedNumberIssue(issues, [...blockPath, "y"], block.y);
    pushBoundedNumberIssue(issues, [...blockPath, "width"], block.width);
    pushBoundedNumberIssue(issues, [...blockPath, "height"], block.height);
    pushBoundedNumberIssue(issues, [...blockPath, "rotation"], block.rotation, 360);

    switch (block.type) {
      case "text":
        pushSafeCssColorIssue(issues, [...blockPath, "fill"], block.fill);
        pushSafeCssColorIssue(issues, [...blockPath, "stroke"], block.stroke);
        pushSafeFontFamilyIssue(issues, [...blockPath, "fontFamily"], block.fontFamily);
        pushBoundedNumberIssue(
          issues,
          [...blockPath, "fontSize"],
          block.fontSize,
          MAX_SAFE_SCENE_FONT_SIZE,
        );
        pushBoundedNumberIssue(
          issues,
          [...blockPath, "lineHeight"],
          block.lineHeight,
          MAX_SAFE_SCENE_LINE_HEIGHT,
        );
        pushBoundedNumberIssue(
          issues,
          [...blockPath, "letterSpacing"],
          block.letterSpacing,
          MAX_SAFE_SCENE_LETTER_SPACING,
        );
        pushBoundedNumberIssue(issues, [...blockPath, "padding"], block.padding);
        if (block.strokeWidth !== undefined) {
          pushBoundedNumberIssue(issues, [...blockPath, "strokeWidth"], block.strokeWidth);
        }
        break;
      case "rect":
        pushSafeCssColorIssue(issues, [...blockPath, "fill"], block.fill);
        pushSafeCssColorIssue(issues, [...blockPath, "stroke"], block.stroke);
        pushBoundedNumberIssue(issues, [...blockPath, "strokeWidth"], block.strokeWidth);
        pushBoundedNumberIssue(issues, [...blockPath, "cornerRadius"], block.cornerRadius);
        break;
      case "frame":
        pushSafeCssColorIssue(issues, [...blockPath, "fill"], block.fill);
        pushSafeCssColorIssue(issues, [...blockPath, "stroke"], block.stroke);
        pushBoundedNumberIssue(issues, [...blockPath, "strokeWidth"], block.strokeWidth);
        pushBoundedNumberIssue(issues, [...blockPath, "cornerRadius"], block.cornerRadius);
        pushBoundedNumberIssue(issues, [...blockPath, "insetPadding"], block.insetPadding);
        break;
      case "shape":
        pushSafeCssColorIssue(issues, [...blockPath, "fill"], block.fill);
        pushSafeCssColorIssue(issues, [...blockPath, "stroke"], block.stroke);
        pushBoundedNumberIssue(issues, [...blockPath, "strokeWidth"], block.strokeWidth);
        pushBoundedNumberIssue(issues, [...blockPath, "cornerRadius"], block.cornerRadius);
        for (const [pointIndex, point] of (block.points ?? []).entries()) {
          pushBoundedNumberIssue(
            issues,
            [...blockPath, "points", pointIndex],
            point,
            MAX_SAFE_SCENE_SHAPE_POINTS,
          );
        }
        break;
      case "image":
      case "logo":
        pushBoundedNumberIssue(issues, [...blockPath, "cornerRadius"], block.cornerRadius);
        break;
      case "group":
        issues.push({
          path: blockPath,
          message: "Scene groups are not supported for safe rendering.",
        });
        break;
    }
  }

  return issues;
};

export const socialPostScenePlanSafetySchema = socialPostScenePlanV1Schema.superRefine(
  (plan, ctx) => {
    for (const issue of collectSocialPostScenePlanSafetyIssues(plan)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: issue.path,
        message: issue.message,
      });
    }
  },
);

export const assertSocialPostScenePlanSafe = (plan: SocialPostScenePlanV1): void => {
  const issues = collectSocialPostScenePlanSafetyIssues(plan);
  if (issues.length > 0) {
    throw new Error(`Unsafe social post scene plan: ${issues[0]?.message ?? "unknown issue"}`);
  }
};
